import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  Box,
  Collapse,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Dashboard,
  SportsCricket,
  Groups,
  EmojiEvents,
  Person,
  Settings,
  AdminPanelSettings,
  ExpandLess,
  ExpandMore,
  Home,
  GamepadOutlined,
  PeopleAlt,
  Stadium,
  AccountBox,
  TrendingUp,
  Assignment,
  BarChart,
  Star,
  Close,
  Logout,
  PersonAdd,
  PlayArrow,
  ManageAccounts,
  Gavel,
  NotificationsOutlined,
  EmailOutlined,
  Analytics,
  CompareArrows,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 280;

const Sidebar = ({ open, onClose, variant = 'temporary' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [openMenus, setOpenMenus] = useState({});

  const handleMenuToggle = (menu) => {
    setOpenMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/auth/login');
  };

  // Main navigation items - simplified and prioritized
  const mainMenuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
      requireAuth: true,
    },
    {
      text: 'My Games',
      icon: <GamepadOutlined />,
      path: '/games/my-games',
      requireAuth: true,
      badge: 'New',
    },
    {
      text: 'Create Game',
      icon: <EmojiEvents />,
      path: '/games/create',
      requireAuth: true,
    },
    {
      text: 'Join Game',
      icon: <PersonAdd />,
      path: '/games/join',
      requireAuth: true,
    },
    {
      text: 'Contests',
      icon: <EmojiEvents />,
      hasSubmenu: true,
      requireAuth: true,
      submenu: [
        { text: 'All Matches', path: '/matches/all' },
        { text: 'All Contests', path: '/contests/all' },
        { text: 'My Contests', path: '/contests/my-contests' },
      ],
    },
    {
      text: 'Players',
      icon: <SportsCricket />,
      path: '/players/all',
      requireAuth: true,
    },
    {
      text: 'Teams',
      icon: <Groups />,
      path: '/teams/all',
      requireAuth: true,
    },
  ];

  // Advanced/Admin menu items
  const advancedMenuItems = [
    {
      text: 'All Games',
      icon: <BarChart />,
      path: '/games/all',
      requireAuth: true,
      adminOnly: true,
    },
    {
      text: 'Contest Management',
      icon: <CompareArrows />,
      hasSubmenu: true,
      requireAuth: true,
      adminOnly: true,
      submenu: [
        { text: 'All Contests', path: '/admin/contests' },
        { text: 'Create Contest', path: '/admin/contests/create' },
        { text: 'Contest Analytics', path: '/admin/contests/analytics' },
      ],
    },
    {
      text: 'Series Management',
      icon: <Star />,
      hasSubmenu: true,
      requireAuth: true,
      adminOnly: true,
      submenu: [
        { text: 'All Series', path: '/series/all' },
        { text: 'Create Series', path: '/series/create' },
      ],
    },
    {
      text: 'Player Management',
      icon: <Assignment />,
      hasSubmenu: true,
      requireAuth: true,
      adminOnly: true,
      submenu: [
        { text: 'Manage Players', path: '/players/manage' },
        { text: 'Player Stats', path: '/players/stats' },
      ],
    },
    {
      text: 'User Management',
      icon: <AdminPanelSettings />,
      hasSubmenu: true,
      requireAuth: true,
      adminOnly: true,
      submenu: [
        { text: 'All Users', path: '/admin/users' },
        { text: 'User Details', path: '/admin/user-details' },
      ],
    },
    {
      text: 'Notification System',
      icon: <NotificationsOutlined />,
      hasSubmenu: true,
      requireAuth: true,
      adminOnly: true,
      submenu: [
        { text: 'Manage Notifications', path: '/admin/notifications' },
        { text: 'Analytics', path: '/admin/notifications/analytics' },
        { text: 'Templates', path: '/admin/notifications/templates' },
      ],
    },
  ];

  const profileMenuItems = [
    {
      text: 'Profile',
      icon: <AccountBox />,
      path: '/profile',
      requireAuth: true,
    },
    {
      text: 'Account Settings',
      icon: <ManageAccounts />,
      path: '/account/settings',
      requireAuth: true,
    },
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/settings',
      requireAuth: true,
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const isActive = (path) => location.pathname === path;
  const isParentActive = (submenu) => submenu?.some(item => isActive(item.path));

  const shouldShowItem = (item) => {
    if (!item.requireAuth) return true;
    if (!isAuthenticated) return false;
    if (item.adminOnly && !isAdmin()) return false;
    return true;
  };

  const renderMenuItem = (item) => {
    if (!shouldShowItem(item)) return null;

    if (item.hasSubmenu) {
      const isMenuActive = isParentActive(item.submenu);
      return (
        <Box key={item.text}>
          <ListItemButton 
            onClick={() => handleMenuToggle(item.text)}
            selected={isMenuActive}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: isMenuActive ? 'inherit' : 'text.primary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
            {openMenus[item.text] ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={openMenus[item.text]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.submenu.map((subItem) => {
                if (!shouldShowItem(subItem)) return null;
                return (
                  <ListItemButton
                    key={subItem.text}
                    sx={{ 
                      pl: 4,
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'secondary.main',
                        color: 'secondary.contrastText',
                      },
                    }}
                    onClick={() => handleNavigation(subItem.path)}
                    selected={isActive(subItem.path)}
                  >
                    <ListItemText primary={subItem.text} />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        </Box>
      );
    }

    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          selected={isActive(item.path)}
          sx={{
            borderRadius: 1,
            mx: 1,
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
          }}
        >
          <ListItemIcon sx={{ color: isActive(item.path) ? 'inherit' : 'text.primary' }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText primary={item.text} />
          {item.badge && (
            <Chip 
              label={item.badge} 
              size="small" 
              color="secondary" 
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <PlayArrow />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            SeriesX
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        )}
      </Box>
      
      <Divider />

      {/* User Info */}
      {isAuthenticated && user && (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user.name?.[0] || user.username?.[0] || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {user.name || user.username || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isAdmin() ? 'Administrator' : 'Player'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Divider />

      {/* Navigation */}
      <Box sx={{ overflow: 'auto', flex: 1, py: 1 }}>
        {/* Main Menu */}
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
          Main
        </Typography>
        <List dense>
          {mainMenuItems.map((item) => renderMenuItem(item))}
        </List>
        
        {/* Advanced/Admin Menu */}
        {isAdmin() && advancedMenuItems.some(item => shouldShowItem(item)) && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
              Administration
            </Typography>
            <List dense>
              {advancedMenuItems.map((item) => renderMenuItem(item))}
            </List>
          </>
        )}
        
        {/* Profile Menu */}
        <Divider sx={{ my: 1 }} />
        <Typography variant="overline" sx={{ px: 2, color: 'text.secondary', fontSize: '0.7rem' }}>
          Account
        </Typography>
        <List dense>
          {profileMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Footer */}
      {isAuthenticated && (
        <>
          <Divider />
          <Box sx={{ p: 1 }}>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 1,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit' }}>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: open ? drawerWidth : 0 }, flexShrink: { md: 0 } }}
    >
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: isMobile ? '85vw' : drawerWidth,
            maxWidth: isMobile ? '320px' : drawerWidth,
            backgroundImage: theme.palette.mode === 'dark' 
              ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.01) 100%)',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;